import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, CircularProgress, Divider, InputAdornment, InputBase, ListItemIcon, ListItemText, Menu,
  MenuItem, MenuList, Popover, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { formatDay } from '../../utils/dates';
import { DEFAULT_RANGE, RANGE_OPTIONS, resolveRange } from './dateRanges';

const PILL_HEIGHT = 36;

/**
 * The filters above a table: the filter pills on the first row; the date range at the start of the second, then
 * Clear all (once any filter is set), with search at its end
 */
export function FilterBar({ children, date, search, onClear, active }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>{children}</Stack>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {date}
        {active && (
          <Button size="small" onClick={onClear} sx={{ height: PILL_HEIGHT, px: 1.5, borderRadius: 999 }}>
            Clear all
          </Button>
        )}
        {search && <Box sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>{search}</Box>}
      </Stack>
    </Stack>
  );
}

/**
 * A filter as a pill of fixed width: "Label value", the value cut with an ellipsis (title, when given, shows in full
 * on hover). active (a filter is set) tints it; the end slot holds the arrow, or the clear button when active and
 * onClear is given, so choosing a value never changes the width.
 */
function FilterPill({ label, value, title, active, icon, onClick, onClear, open, width = 200 }) {
  const clearable = active && onClear;
  return (
    <Tooltip title={title && active ? title : ''} placement="bottom-start" enterDelay={400}>
      <Box sx={(theme) => ({
        display: 'inline-flex', alignItems: 'center', height: PILL_HEIGHT, width, maxWidth: '100%', borderRadius: 999,
        border: 1, borderColor: active ? alpha(theme.palette.primary.main, 0.35) : 'divider',
        bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        boxShadow: open ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` : '0 1px 2px rgba(37, 45, 82, 0.04)',
        transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
        '&:hover': { borderColor: active ? alpha(theme.palette.primary.main, 0.5) : theme.palette.grey[400] },
      })}>
        <ButtonBase onClick={onClick} aria-haspopup="true" aria-expanded={open}
                    sx={{ flex: 1, minWidth: 0, height: '100%', pl: 1.75, pr: clearable ? 0 : 0.5, gap: 0.75,
                      justifyContent: 'flex-start', borderRadius: 999, fontSize: 13.5 }}>
          {icon}
          <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</Box>
          {/* a block, so text values get the ellipsis; status labels (dot + text) stay inline inside it */}
          <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'block', overflow: 'hidden', whiteSpace: 'nowrap',
            textOverflow: 'ellipsis', textAlign: 'left', fontWeight: 500,
            color: active ? 'primary.main' : 'text.primary', '& > *': { verticalAlign: 'middle' } }}>
            {value}
          </Box>
          {!clearable && <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.75, flexShrink: 0 }} />}
        </ButtonBase>
        {clearable && (
          <ButtonBase onClick={onClear} aria-label={`Clear ${label}`}
                      sx={{ width: 26, height: 26, mx: 0.5, flexShrink: 0, borderRadius: '50%', color: 'primary.main',
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
      </Box>
    </Tooltip>
  );
}

// Updates the query a moment after typing stops
export function SearchFilter({ value = '', onChange, placeholder = 'Search' }) {
  const [text, setText] = useState(value);
  const latest = useRef(value);

  useEffect(() => {
    latest.current = value;
    setText(value);
  }, [value]);

  useEffect(() => {
    if (text === latest.current) return undefined;
    const timer = setTimeout(() => onChange(text.trim()), 350);
    return () => clearTimeout(timer);
  }, [text, onChange]);

  return (
    <InputBase value={text} onChange={(event) => setText(event.target.value)} placeholder={placeholder}
               inputProps={{ 'aria-label': placeholder }}
               startAdornment={<SearchIcon sx={{ fontSize: 20, color: 'text.primary', opacity: 0.7, mr: 1 }} />}
               endAdornment={text && (
                 <ButtonBase onClick={() => setText('')} aria-label="Clear search"
                             sx={{ borderRadius: '50%', p: 0.25, color: 'text.secondary' }}>
                   <CloseIcon sx={{ fontSize: 16 }} />
                 </ButtonBase>
               )}
               // styled like the filter pills: a grey fill would read as disabled
               sx={(theme) => ({
                 height: PILL_HEIGHT, width: { xs: '100%', sm: 400 }, px: 1.5, borderRadius: 999, fontSize: 14,
                 bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                 boxShadow: '0 1px 2px rgba(37, 45, 82, 0.04)',
                 transition: 'box-shadow 0.15s, border-color 0.15s',
                 '&:hover': { borderColor: theme.palette.grey[400] },
                 '&.Mui-focused': { borderColor: alpha(theme.palette.primary.main, 0.5),
                   boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` },
                 '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
               })} />
  );
}

/**
 * Single choice from a menu; '' is "all" (allLabel; null when there is always a value). options: [{ value, label }]
 */
export function SelectFilter({ label, value = '', options, onChange, allLabel = 'All', width = 180 }) {
  const [anchor, setAnchor] = useState(null);
  const selected = options.find((option) => option.value === value);
  const choose = (next) => {
    setAnchor(null);
    onChange(next);
  };

  return (
    <>
      <FilterPill label={label} value={selected ? selected.label : allLabel} open={Boolean(anchor)} width={width}
                  title={typeof selected?.label === 'string' ? selected.label : undefined}
                  active={Boolean(allLabel) && Boolean(selected)} onClick={(event) => setAnchor(event.currentTarget)}
                  onClear={allLabel ? () => onChange('') : undefined} />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            slotProps={{ paper: { sx: { mt: 0.75, minWidth: 200, borderRadius: 2 } } }}>
        {allLabel && <OptionItem label={allLabel} selected={!selected} onClick={() => choose('')} />}
        {options.map((option) => (
          <OptionItem key={option.value} label={option.label} selected={option.value === value}
                      onClick={() => choose(option.value)} />
        ))}
      </Menu>
    </>
  );
}

function OptionItem({ label, selected, onClick }) {
  return (
    <MenuItem selected={selected} onClick={onClick} sx={{ fontSize: 14, py: 0.75 }}>
      <ListItemText primaryTypographyProps={{ fontSize: 14, component: 'span',
        sx: { display: 'inline-flex', alignItems: 'center' } }}>
        {label}
      </ListItemText>
      <ListItemIcon sx={{ minWidth: 0, ml: 2, visibility: selected ? 'visible' : 'hidden' }}>
        <CheckIcon fontSize="small" color="primary" />
      </ListItemIcon>
    </MenuItem>
  );
}

/**
 * Preset ranges, or since / until for a custom one
 */
export function DateRangeFilter({ query, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const range = query.range || DEFAULT_RANGE;
  const { since, until } = resolveRange(query);
  const [custom, setCustom] = useState(null);
  const option = RANGE_OPTIONS.find((item) => item.value === range);
  const value = range === 'custom' ? `${formatShortDay(since)} – ${formatShortDay(until)}` : option?.label;

  const close = () => {
    setAnchor(null);
    setCustom(null);
  };
  const choose = (next) => {
    if (next === 'custom') {
      setCustom({ since, until });
      return;
    }
    close();
    onChange({ range: next, since: null, until: null });
  };

  return (
    <>
      <FilterPill label="Date" value={value} open={Boolean(anchor)} active={range !== DEFAULT_RANGE} width={230}
                  title={range === 'custom' ? `${formatDay(since)} – ${formatDay(until)}` : undefined}
                  icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                  onClick={(event) => setAnchor(event.currentTarget)}
                  onClear={() => onChange({ range: null, since: null, until: null })} />
      <Popover anchorEl={anchor} open={Boolean(anchor)} onClose={close}
               slotProps={{ paper: { sx: { mt: 0.75, borderRadius: 2, minWidth: 220 } } }}>
        {custom ? (
          <Stack spacing={1.5} sx={{ p: 2, width: 280 }}>
            <Typography variant="subtitle2">Custom range</Typography>
            <TextField size="small" type="date" label="From" value={custom.since} InputLabelProps={{ shrink: true }}
                       inputProps={{ max: custom.until }}
                       onChange={(event) => setCustom({ ...custom, since: event.target.value })} />
            <TextField size="small" type="date" label="To" value={custom.until} InputLabelProps={{ shrink: true }}
                       inputProps={{ min: custom.since }}
                       onChange={(event) => setCustom({ ...custom, until: event.target.value })} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setCustom(null)}>Back</Button>
              <Button size="small" variant="contained" disabled={!custom.since || !custom.until}
                      onClick={() => {
                        onChange({ range: 'custom', ...custom });
                        close();
                      }}>
                Apply
              </Button>
            </Stack>
          </Stack>
        ) : (
          <MenuList autoFocusItem sx={{ p: 0.5 }}>
            {RANGE_OPTIONS.flatMap((item) => [
              item.value === 'custom' && <Divider key="divider" sx={{ my: 0.5 }} />,
              <OptionItem key={item.value} label={item.label} selected={item.value === range}
                          onClick={() => choose(item.value)} />,
            ]).filter(Boolean)}
          </MenuList>
        )}
      </Popover>
    </>
  );
}

/**
 * Pick one ad account, campaign or ad set by name from a searchable list. value is its id (from the URL); its name
 * is fetched once. fetchList is the AdsApi list call and params narrow the options (e.g. the selected ad account).
 */
export function EntityFilter({ label, value, onChange, fetchList, params, describe, width = 230 }) {
  const [anchor, setAnchor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchor);
  const paramsKey = JSON.stringify(params || {});
  const baseParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);

  // name of the selected id, e.g. after following a link
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let current = true;
    fetchList({ id: value, page_size: 1 }).then((data) => current && setSelected(data.results[0] ?? null))
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [value, selected, fetchList]);

  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchList({ ...baseParams, search: input, ordering: 'name', page_size: 20 })
        .then((data) => current && setOptions(data.results))
        .catch(() => current && setOptions([]))
        .finally(() => current && setLoading(false));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [open, input, baseParams, fetchList]);

  const close = () => {
    setAnchor(null);
    setInput('');
  };
  const choose = (option) => {
    setSelected(option);
    onChange(option ? option.id : null);
    close();
  };
  const name = (option) => option.name || option.object_id;

  return (
    <>
      <FilterPill label={label} value={selected ? name(selected) : 'All'} active={Boolean(value)} open={open}
                  width={width} title={selected ? name(selected) : undefined}
                  onClick={(event) => setAnchor(event.currentTarget)} onClear={() => choose(null)} />
      <Popover anchorEl={anchor} open={open} onClose={close}
               slotProps={{ paper: { sx: { mt: 0.75, width: 320, borderRadius: 2 } } }}>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <InputBase autoFocus fullWidth value={input} onChange={(event) => setInput(event.target.value)}
                     placeholder={`Search ${label.toLowerCase()}s`} inputProps={{ 'aria-label': `Search ${label}` }}
                     startAdornment={(
                       <InputAdornment position="start">
                         {loading ? <CircularProgress size={16} /> : <SearchIcon sx={{ fontSize: 18 }} />}
                       </InputAdornment>
                     )}
                     sx={{ px: 1, height: 36, borderRadius: 1.5, bgcolor: 'action.hover', fontSize: 14 }} />
        </Box>
        <MenuList sx={{ p: 0.5, maxHeight: 320, overflowY: 'auto' }}>
          <OptionItem label={`All ${label.toLowerCase()}s`} selected={!value} onClick={() => choose(null)} />
          {options.map((option) => (
            <OptionItem key={option.id} selected={String(option.id) === String(value)} onClick={() => choose(option)}
                        label={(
                          <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
                            <Typography variant="body2" noWrap component="span" sx={{ display: 'block' }}>
                              {name(option)}
                            </Typography>
                            {describe && (
                              <Typography variant="caption" color="text.secondary" noWrap component="span"
                                          sx={{ display: 'block' }}>
                                {describe(option)}
                              </Typography>
                            )}
                          </Box>
                        )} />
          ))}
          {!loading && options.length === 0 && (
            <MenuItem disabled sx={{ fontSize: 14 }}>No matches</MenuItem>
          )}
        </MenuList>
      </Popover>
    </>
  );
}

const shortDayFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

// YYYY-MM-DD -> 19 Sept, for the date pill
function formatShortDay(value) {
  const [year, month, day] = value.split('-').map(Number);
  return shortDayFormat.format(new Date(year, month - 1, day));
}
